// Клиент API пульта (задача D4): единственное место, где пульт делает fetch к `ai-qual-api`.
//
// - Каждый ответ разбирается схемой контракта (`@contract`): данные — `T`, ошибка — `ApiError` с текстом сервера.
// - Сервис не ответил (сеть, 502/503/504 от прокси, тело не JSON — nginx отдал свою страницу) —
//   `ServiceUnavailableError`, связь отмечается сбоем. Любой JSON-ответ самого сервиса, даже ошибка, — «связь есть»:
//   502 `omni_unavailable` значит, что не ответил Омнидеск, а сервис Анны на месте.
// - Любой 401, кроме входа и выхода, открывает окно «Сессия истекла». На входе 401 — это «неверный пароль», на выходе —
//   «сессии и так нет».
// - Отменённый запрос (TanStack Query отменил, экран закрылся) — `AbortError` как есть: это не сбой связи.
import { ApiErrorBodySchema } from '@contract/index.js';
import type { z } from 'zod';
import { emitSessionExpired } from '@/auth/session';
import { markFail, markOk } from './connection';
import { ApiError, ContractError, ServiceUnavailableError } from './errors';

export const LOGIN_PATH = '/api/v1/auth/login';
export const LOGOUT_PATH = '/api/v1/auth/logout';

/** 401 на этих путях — не «Сессия истекла». */
const NO_SESSION_EVENT = new Set<string>([LOGIN_PATH, LOGOUT_PATH]);

/** Ответы прокси: nginx перед `ai-qual-api`, сам сервис их не отдаёт без тела в форме контракта. */
const GATEWAY_STATUSES = new Set([502, 503, 504]);

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export function apiGet<T>(path: string, schema: z.ZodType<T>, o: { signal?: AbortSignal } = {}): Promise<T> {
  return request('GET', path, undefined, schema, o.signal);
}

export function apiSend<T>(method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body: unknown, schema: z.ZodType<T>): Promise<T> {
  return request(method, path, body, schema, undefined);
}

function isAbort(error: unknown, signal: AbortSignal | undefined): boolean {
  return signal?.aborted === true || (error instanceof Error && error.name === 'AbortError');
}

/** Сбой на пути к сервису: отмену отдаём как есть, остальное — «Сервис Анны не отвечает». */
function unreachable(error: unknown, signal: AbortSignal | undefined, status: number | null): unknown {
  if (isAbort(error, signal)) return error;
  markFail(Date.now());
  return new ServiceUnavailableError({ status, cause: error });
}

const NOT_JSON = Symbol('not-json');

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return NOT_JSON;
  }
}

async function request<T>(
  method: Method,
  path: string,
  body: unknown,
  schema: z.ZodType<T>,
  signal: AbortSignal | undefined,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const init: RequestInit = { method, credentials: 'same-origin', headers };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  if (signal !== undefined) init.signal = signal;

  let response: Response;
  let text: string;
  try {
    response = await fetch(path, init);
  } catch (error) {
    throw unreachable(error, signal, null);
  }
  try {
    text = await response.text();
  } catch (error) {
    throw unreachable(error, signal, response.status);
  }

  const pathname = path.split('?', 1)[0] ?? path;
  if (response.status === 401 && !NO_SESSION_EVENT.has(pathname)) emitSessionExpired();

  const json = parseJson(text);
  if (json === NOT_JSON) {
    markFail(Date.now());
    throw new ServiceUnavailableError({ status: response.status });
  }

  if (!response.ok) {
    const failure = ApiErrorBodySchema.safeParse(json);
    if (failure.success) {
      markOk(Date.now());
      throw new ApiError(response.status, failure.data.error.code, failure.data.error.messageRu);
    }
    if (GATEWAY_STATUSES.has(response.status)) {
      markFail(Date.now());
      throw new ServiceUnavailableError({ status: response.status });
    }
    markOk(Date.now());
    throw new ContractError({ path, issues: failure.error.issues });
  }

  markOk(Date.now());
  const parsed = schema.safeParse(json);
  if (!parsed.success) throw new ContractError({ path, issues: parsed.error.issues });
  return parsed.data;
}
